import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { ActionIcon } from "@mantine/core"
import { IconPlayerPauseFilled, IconPlayerPlayFilled } from "@tabler/icons-react"
import type { CSSProperties } from "react"
import { fmtTimestamp } from "../../MeetingSummary/helpers"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

function fillStyle(percent: number): CSSProperties {
  return { width: `${percent}%` }
}

/**
 * Docked transport for a narrow meeting page. Props in, play / pause / speed /
 * expand out. The audio element stays in the full player.
 */
export function MiniPlayerView({
  playing,
  currentTime,
  duration,
  speed,
  onPlay,
  onPause,
  onSpeed,
  onExpand,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  const percent = duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0
  const speedLabel = i18n._(msg`Playback speed ${speed}`)

  return (
    <div className={classes.dock} data-testid="mini-player">
      <div className={classes.track} aria-hidden>
        <span className={classes.fill} style={fillStyle(percent)} />
      </div>
      <ActionIcon
        className={classes.play}
        variant="filled"
        color="brand"
        radius="xl"
        size="lg"
        aria-label={playing ? i18n._(msg`Pause`) : i18n._(msg`Play`)}
        onClick={playing ? onPause : onPlay}
      >
        {playing ? <IconPlayerPauseFilled size={16} /> : <IconPlayerPlayFilled size={16} />}
      </ActionIcon>
      <button type="button" className={classes.expand} onClick={onExpand}>
        <span>
          <Trans>Recording</Trans>
        </span>
        <span className={classes.time}>
          {fmtTimestamp(currentTime)} / {fmtTimestamp(duration)}
        </span>
      </button>
      <button type="button" className={classes.speed} aria-label={speedLabel} onClick={onSpeed}>
        {speed}×
      </button>
    </div>
  )
}
